import { z } from 'zod'

/**
 * Runtime configuration, validated once at startup so a typo in a `.env` file
 * fails loudly here instead of as a 404 somewhere deep in a feature.
 */
const envSchema = z.object({
  VITE_API_BASE_URL: z.string().min(1).default('/api'),
  /** Empty means there is no realtime backend yet; the app opens no socket. */
  VITE_WS_URL: z.string().default(''),
  VITE_APP_NAME: z.string().min(1).default('Studio Workspace'),
  /** Runs the app against an in-memory demo session instead of the backend. */
  VITE_DEMO_MODE: z.enum(['true', 'false']).default('false'),
  VITE_COMPLIANCE_DEVELOPMENT: z.enum(['true', 'false']).default('false'),
  VITE_SHARED_SHOW: z.enum(['true', 'false']).default('false'),
})

const parsed = envSchema.safeParse(import.meta.env)

if (!parsed.success) {
  const details = parsed.error.issues.map((i) => `  - ${i.path.join('.')}: ${i.message}`).join('\n')
  throw new Error(`Invalid environment configuration:\n${details}`)
}

export const env = {
  apiBaseUrl: parsed.data.VITE_API_BASE_URL,
  wsUrl: parsed.data.VITE_WS_URL,
  /**
   * Whether to open the STOMP socket at all. Without an endpoint behind it,
   * the client retries every four seconds for the life of the session — so
   * an unset `VITE_WS_URL` means "no realtime yet", not "use the default".
   */
  realtimeEnabled: parsed.data.VITE_WS_URL !== '',
  appName: parsed.data.VITE_APP_NAME,
  // Standalone fixtures never replace the team's ordinary demo/live sources.
  complianceDevelopment:
    parsed.data.VITE_COMPLIANCE_DEVELOPMENT === 'true' || parsed.data.VITE_SHARED_SHOW === 'true',
  sharedShow: parsed.data.VITE_SHARED_SHOW === 'true',
  demoMode:
    parsed.data.VITE_DEMO_MODE === 'true' ||
    parsed.data.VITE_COMPLIANCE_DEVELOPMENT === 'true' ||
    parsed.data.VITE_SHARED_SHOW === 'true',
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,
} as const

/**
 * Turns the configured WebSocket path into an absolute URL.
 * A relative value (`/ws`) is resolved against the current origin so the Vite
 * proxy handles it in dev and the reverse proxy handles it in production.
 */
export function resolveWebSocketUrl(url: string = env.wsUrl): string {
  if (/^wss?:\/\//i.test(url)) return url
  if (/^https?:\/\//i.test(url)) return url.replace(/^http/i, 'ws')

  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  const path = url.startsWith('/') ? url : `/${url}`
  return `${protocol}//${window.location.host}${path}`
}

/**
 * The server sends paths that start with `/api` (the logo URL). Resolve them against the
 * configured API base, so a non-default `VITE_API_BASE_URL` still reaches the right host.
 */
export function resolveApiUrl(path: string): string {
  return path.startsWith('/api') ? `${env.apiBaseUrl}${path.slice('/api'.length)}` : path
}
